import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { Link, useParams } from "react-router";

import { Card, CardContent } from "@/components/ui/card";
import { HELP_ARTICLES, helpArticle } from "./helpArticles";

/**
 * /help/:slug - one knowledge-base article: title, read time, the body
 * (deep links into the app throughout), and related articles below.
 */
export const HelpArticle = () => {
  const { slug = "" } = useParams();
  const article = helpArticle(slug);

  if (!article) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10 text-center flex flex-col gap-3">
        <BookOpen className="w-8 h-8 mx-auto text-muted-foreground" />
        <h1 className="text-lg font-semibold">Article not found</h1>
        <p className="text-sm text-muted-foreground">
          That page may have moved.{" "}
          <Link to="/help" className="underline hover:no-underline">
            Browse the help center
          </Link>{" "}
          to find what you need.
        </p>
      </div>
    );
  }

  const related = article.related
    .map((s) => helpArticle(s))
    .filter((a): a is NonNullable<typeof a> => !!a);
  const idx = HELP_ARTICLES.findIndex((a) => a.slug === article.slug);
  const next = HELP_ARTICLES[idx + 1];

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 flex flex-col gap-4 pb-16">
      <Link
        to="/help"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground self-start"
      >
        <ArrowLeft className="w-3 h-3" /> Help center
      </Link>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
          {article.category} · {article.minutes} min read
        </p>
        <h1 className="text-2xl font-semibold leading-tight">{article.title}</h1>
        <p className="text-sm text-muted-foreground mt-2">{article.summary}</p>
      </div>

      <div className="flex flex-col gap-4 border-t border-border pt-4">
        {article.body}
      </div>

      {related.length > 0 && (
        <Card className="mt-2">
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
              Keep reading
            </p>
            <div className="flex flex-col gap-1.5">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/help/${a.slug}`}
                  className="flex items-center gap-2 text-sm hover:underline"
                >
                  <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="flex-1">{a.title}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {a.minutes} min
                  </span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {next && (
        <Link
          to={`/help/${next.slug}`}
          className="text-xs text-muted-foreground hover:text-foreground self-end inline-flex items-center gap-1"
        >
          Next article: {next.title} <ArrowRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
};
